/* Ordering and summary statistics over records.
 *
 * Separate from format.ts on purpose: that file turns a value into characters
 * for a human, this one decides which records come first and what a set of
 * them adds up to. Both surfaces need identical answers — a median latency
 * quoted on the desk and the same number quoted off a phone have to agree, or
 * the KPI is two different numbers with one name.
 */

/* ── Ordering ─────────────────────────────────────────────────────────────── */

/**
 * Newest first, by an ISO timestamp.
 *
 * The idiom this replaces was `(a, b) => (a.x < b.x ? 1 : -1)`, written out
 * four times across the two apps. It never returns 0, which makes it an
 * inconsistent comparator: two records captured in the same millisecond — one
 * burst of photos, one seeded fixture — each compare as "after" the other, and
 * the sort is entitled to any order it likes. Rare, but the failure is a list
 * that reshuffles between renders for no visible reason, which reads as a bug
 * in sync rather than in sorting.
 */
export const byNewest =
  <T>(key: (item: T) => string) =>
  (a: T, b: T): number => {
    const [x, y] = [key(a), key(b)];
    return x < y ? 1 : x > y ? -1 : 0;
  };

/** Oldest first. Same contract, opposite direction. */
export const byOldest =
  <T>(key: (item: T) => string) =>
  (a: T, b: T): number => {
    const [x, y] = [key(a), key(b)];
    return x < y ? -1 : x > y ? 1 : 0;
  };

/** The common case: anything with a `captured_at`, newest first. */
export const newestFirst = byNewest<{ captured_at: string }>((e) => e.captured_at);

/* ── Summary ──────────────────────────────────────────────────────────────── */

/**
 * Median, or null for an empty set.
 *
 * Null rather than 0 because these feed KPI dials, and a dial reading zero
 * means "measured, and it was zero" where null means "nothing to measure yet".
 * Conflating them turns an empty first week into a reported latency of nought.
 *
 * Copies before sorting — callers pass arrays they still intend to use.
 */
export const median = (ns: readonly number[]): number | null => {
  if (!ns.length) return null;
  const s = [...ns].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  // Even-length sets average the middle pair rather than leaning high, so the
  // number does not jump by a whole step each time one record arrives.
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/** Percentage, or null when there is nothing to take a percentage of. */
export const percentOf = (part: number, whole: number): number | null =>
  whole ? Math.round((part / whole) * 100) : null;

/** Count occurrences into a Map, keyed by whatever the selector returns. */
export const tally = <T, K>(items: readonly T[], key: (item: T) => K): Map<K, number> => {
  const out = new Map<K, number>();
  for (const item of items) {
    const k = key(item);
    out.set(k, (out.get(k) ?? 0) + 1);
  }
  return out;
};
